import React, { useCallback, useEffect, useRef, useState } from 'react';
import { buildCheckMenuItems } from '../../helpers/contextMenuHelpers';
import Modal from './Modal';
import ContextMenu from './ContextMenu';
import AppInput from '../Inputs/AppInput';
import Icon from '../Icon';
import {
  apiListProjectMembers,
  apiShareProjectWithEmail,
  apiRemoveProjectMember,
  apiChangeMemberRole
} from '../../api/projects';
import {
  apiCreateShareLink,
  apiGetShareLinksForTarget,
  apiRevokeShareLink,
  buildShareUrl,
} from '../../api/shareLinks';
import emailHelpers from '../../helpers/emailHelpers';

const ROLES = [
  { value: 'editor', label: 'Editor' },
  { value: 'viewer', label: 'Viewer' },
];

const PUBLIC_LINK_ROLES = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'editor', label: 'Editor' },
];

const roleLabel = (roles, v) => roles.find(r => r.value === v)?.label || roles[0]?.label || 'Viewer';

// Role/action dropdown â€” uses the shared ContextMenu with portal positioning,
// matching the same mechanism as CaretButton in the toolbar.
function RoleDropdown({ value, onChange, onRemove, roles = ROLES }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);

  const items = [
    ...buildCheckMenuItems({
      options: roles,
      selectedValue: value,
      onSelect: (nextValue) => onChange(nextValue),
      onAfterSelect: () => setOpen(false),
      getValue: (role) => role.value,
      getLabel: (role) => role.label,
    }),
    ...(onRemove ? [
      { divider: true },
      { label: 'Remove', iconClass: 'user-minus', className: 'shareRoleRemove', onClick: onRemove },
    ] : []),
  ];

  return (
    <div>
      <button
        ref={btnRef}
        type="button"
        className="caretTextButton"
        data-context-placement="bottom"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
      >
        {roleLabel(roles, value)}
        <Icon icon={open ? 'caret-up' : 'caret-down'} className="icon caretIcon small" />
      </button>
      {open && (
        <ContextMenu
          items={items}
          triggerRef={btnRef}
          onClose={() => setOpen(false)}
          placement="bottom"
          menuClass="shareRoleMenuCtx"
        />
      )}
    </div>
  );
}


const normalizeEmail = (email = '') => (email || '').trim().toLowerCase();

export default function ModalShareProject({ isOpen, onClose, project, user }) {
  const [recipients, setRecipients] = useState([{ email: '', role: 'editor' }]);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState('');

  // Public share link state
  const [publicLink, setPublicLink] = useState(null);
  const [publicLinkCreating, setPublicLinkCreating] = useState(false);
  const [publicLinkCopied, setPublicLinkCopied] = useState(false);
  const [publicLinkRole, setPublicLinkRole] = useState('viewer');
  const publicCopyTimeoutRef = useRef(null);

  const loadMembers = useCallback(async () => {
    if (!project?.id) return;
    setLoadingMembers(true);
    try {
      const data = await apiListProjectMembers(project.id);
      setMembers(data || []);
    } catch {
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  }, [project?.id]);

  const loadPublicLink = useCallback(async () => {
    if (!project?.id) return;
    try {
      const links = await apiGetShareLinksForTarget('project', project.id);
      const active = links.find(l => l.enabled) || links[0] || null;
      setPublicLink(active || null);
      setPublicLinkRole(active?.visual_state?.publicRole || 'viewer');
    } catch {
      // ignore â€” non-critical
    }
  }, [project?.id]);

  useEffect(() => {
    if (!isOpen) return;
    setRecipients([{ email: '', role: 'editor' }]);
    setError('');
    setPublicLink(null);
    setPublicLinkRole('viewer');
    setPublicLinkCopied(false);
    loadMembers();
    loadPublicLink();
  }, [isOpen, project?.id, loadMembers, loadPublicLink]);

  useEffect(() => {
    return () => clearTimeout(publicCopyTimeoutRef.current);
  }, []);

  const updateRecipient = (idx, field, val) =>
    setRecipients(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));

  const addRecipient = () =>
    setRecipients(prev => [...prev, { email: '', role: 'editor' }]);

  const removeRecipient = (idx) =>
    setRecipients(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  const handleShare = async () => {
    const valid = recipients.filter(r => normalizeEmail(r.email));
    if (!valid.length || !project?.id || !user?.id) return;

    setSharing(true);
    setError('');
    try {
      for (const { email, role } of valid) {
        const addr = normalizeEmail(email);
        await apiShareProjectWithEmail(project.id, addr, user.id, role);
        try {
          await emailHelpers.sendEmailTrigger({
            templateName: 'invite_existing',
            to: addr,
            vars: {
              InviterName: user?.name || user?.email || 'A teammate',
              ProjectName: project.name || 'a project',
              AcceptUrl: `${window.location.origin}/projects/${project.id}`,
            },
            subject: `You've been invited to collaborate on ${project.name || 'a project'}`,
          });
        } catch (emailErr) {
          // eslint-disable-next-line no-console
          console.warn('[ShareProject] Email trigger failed:', emailErr);
        }
      }
      setRecipients([{ email: '', role: 'editor' }]);
      await loadMembers();
    } catch {
      setError('Could not share this project. Please try again.');
    } finally {
      setSharing(false);
    }
  };

  const handleRoleChange = async (member, newRole) => {
    setMembers(prev => prev.map(m => m.id === member.id ? { ...m, access_level: newRole } : m));
    await apiChangeMemberRole(member.id, newRole);
  };

  const handleRemove = async (member) => {
    try {
      await apiRemoveProjectMember(project.id, member.id);
      await loadMembers();
    } catch {
      setError('Could not remove this person from the project.');
    }
  };

  const hasValidRecipient = recipients.some(r => normalizeEmail(r.email));

  const handleCreatePublicLink = async () => {
    if (!project?.id || !user?.id) return;
    setPublicLinkCreating(true);
    try {
      const { link } = await apiCreateShareLink('project', project.id, user.id, null);
      setPublicLink(link);
      setPublicLinkRole('viewer');
    } catch {
      // silently ignore
    } finally {
      setPublicLinkCreating(false);
    }
  };

  const handleCopyPublicLink = async () => {
    const url = buildShareUrl(publicLink);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setPublicLinkCopied(true);
      clearTimeout(publicCopyTimeoutRef.current);
      publicCopyTimeoutRef.current = setTimeout(() => setPublicLinkCopied(false), 2000);
    } catch {
      // ignore clipboard errors
    }
  };

  const handlePublicLinkRoleChange = (newRole) => {
    setPublicLinkRole(newRole);
  };

  const handleRevokePublicLink = async () => {
    if (!publicLink) return;
    try {
      await apiRevokeShareLink(publicLink.id);
      setPublicLink(null);
      setPublicLinkRole('viewer');
    } catch {
      // silently ignore
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title={project?.name ? `Share "${project.name}"` : 'Share project'}
      message="Invite people to collaborate on this project."
      confirmText={sharing ? 'Sharing...' : 'Share'}
      confirmDisabled={sharing || !hasValidRecipient}
      cancelText="Cancel"
      onConfirm={handleShare}
      onCancel={onClose}
    >
      <div className="modalBlock">
          {recipients.map((rec, idx) => (
            <div key={idx} className="modalRow">
              <div className="modalRowContent">
                <AppInput
                  type="email"
                  className="input shareRecipientEmail"
                  value={rec.email}
                  placeholder="name@company.com"
                  onChange={e => updateRecipient(idx, 'email', e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleShare(); } }}
                  autoFocus={idx === 0}
                />
                <div className="modalRowControl">
                  <RoleDropdown
                    value={rec.role}
                    onChange={val => updateRecipient(idx, 'role', val)}
                  />
                  {recipients.length > 1 && (
                    <button
                      type="button"
                      className="iconButton"
                      aria-label="Remove recipient"
                      onClick={() => removeRecipient(idx)}
                    >
                      <Icon icon={'x'} className="icon x" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        <button type="button" className="shareAddAnother  linkButton" onClick={addRecipient}>
          <Icon icon={'plus'} className="icon plus" />
          Add another
        </button>
      </div>

      {error && <p className="shareModalError">{error}</p>}

      <div className="shareModalMembers">
        <h3>Shared with</h3>
        {loadingMembers && <p className="placeholder">Loading people...</p>}
        {!loadingMembers && members.length === 0 && (
          <p className="placeholder">No one else has access yet.</p>
        )}
        {!loadingMembers && members.map(member => (
          <div key={member.id} className="modalRow">
            <div className="modalRowLabel" title={member.display_email}>
              {member.display_name !== member.display_email
                ? member.display_name
                : member.display_email}
              <span className={`shareMemberMeta ${member.invite_status === 'accepted' ? 'accepted' : 'invited'}`} style={{ marginLeft: 8 }}>
                {member.invite_status === 'accepted' ? 'Accepted' : 'Invited'}
              </span>
            </div>
            <div className="modalRowControl">
              <RoleDropdown
                value={member.access_level || 'editor'}
                onChange={val => handleRoleChange(member, val)}
                onRemove={() => handleRemove(member)}
              />
            </div>
          </div>
        ))}
      </div>

      {/* â”€â”€ Public share link â”€â”€ */}
      <div className="modalSeparator" />
      <div className="modalBlock">
        <h3>Public share link</h3>
        {!publicLink ? (
          <button
            type="button"
            className="shareAddAnother linkButton"
            onClick={handleCreatePublicLink}
            disabled={publicLinkCreating}
          >
            <Icon icon={'plus'} className="icon plus" />
            {publicLinkCreating ? 'Creating...' : 'Create share link'}
          </button>
        ) : (
          <>
            <div className="sharePublicLinkUrlRow">
              <span className="sharePublicLinkUrl">{buildShareUrl(publicLink)}</span>
              <button
                type="button"
                className="iconButton"
                aria-label="Copy link"
                onClick={handleCopyPublicLink}
              >
                <Icon icon={publicLinkCopied ? 'check' : 'copy'} className={`icon ${publicLinkCopied ? 'check' : 'copy'}`} />
              </button>
            </div>
            <div className="modalRow">
              <div className="modalRowLabel">Anyone on the internet with the link</div>
              <div className="modalRowControl">
                <RoleDropdown
                  roles={PUBLIC_LINK_ROLES}
                  value={publicLinkRole}
                  onChange={handlePublicLinkRoleChange}
                  onRemove={handleRevokePublicLink}
                />
              </div>
            </div>
            {publicLinkRole === 'editor' && (
              <p className="sharePublicLinkNote">
                Only users with a Ghost account can make edits.
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

